import { SPLIT_CONFIG_TYPES } from '../../../../core/constants';
import { getSplitsBasedOnSplitType } from '../../../../utilities/sectionRefHelpers';
import useStyles from '../../styles';
import useCommonStyles from '../../../../commonStyles';

import { Box } from '@mui/system';
import Typography from '@mui/material/Typography';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Chip, Grid } from '@mui/material';
import { useContext, useMemo } from 'react';
import InputConfig from '../../../../Components/InputConfig';
import { truncateText } from '../../../../utilities/helpers';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import { getSectionNameToDisplay } from './ExperimentsContextSettings/helpers';

const { EXPERIMENT_SPLIT, NORMAL_SPLIT, MULTI_SPLIT } = SPLIT_CONFIG_TYPES;
const commonLabelStyles = { fontSize: '1.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const VideoSectionSplitCard = ({
	units,
	videoSectionId,
	sectionSplitData,
	updateNormalTypeSplitPercentage,
	updateExperimentTypeSplitPercentage,
	deleteSectionFromSplitConfig
}) => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	const {
		controlSection,
		experimentSection,
		multiSplitSections = []
	} = getSplitsBasedOnSplitType(units, videoSectionId, sectionSplitData.type);

	const { expbDoc } = useContext(UnitsContext);

	const splitPercentageMap = useMemo(() => {
		if (!sectionSplitData?.controlExperimentSplits) return {};
		return sectionSplitData.controlExperimentSplits;
	}, [sectionSplitData]);

	const getSplitPercentage = (section) => {
		if (!section || !section.sectionRef) return 0;
		return splitPercentageMap[section.sectionRef]?.splitPercentage || 0;
	};

	const experimentData = useMemo(() => {
		if (
			(sectionSplitData.type !== EXPERIMENT_SPLIT && sectionSplitData.type !== MULTI_SPLIT) ||
			!Array.isArray(expbDoc.experiments)
		) return null;

		return expbDoc.experiments.find(exp => exp.controlVideoSectionId === videoSectionId && exp.status !== "CONCLUDED") || null;
	}, [sectionSplitData.type, expbDoc.experiments, videoSectionId]);

	const multiSplitData = sectionSplitData.type === MULTI_SPLIT ? experimentData : null;

	return (
		<Box className={commonStyleClasses.basicOutlinedBox} style={{ width: '95%' }}>
			<Box style={{ width: '90%' }}>
				<Chip label={sectionSplitData.type} color="primary" style={{ float: 'right' }} />
			</Box>

			{videoSectionId && sectionSplitData && (
				<Box marginTop={'6rem'}>
					{/* Video Section Split Card for EXPERIMENT_SPLIT */}
					{sectionSplitData.type === EXPERIMENT_SPLIT && (
						<Grid container spacing={1} alignItems="center" style={{ width: '100%' }}>
							<Grid item xs={12} >
								<Typography variant='h6'>Split Description</Typography>
								<Typography style={{ fontSize: '1.7rem', color: '#514848', fontWeight: 400 }}>
									{experimentData?.comment ?? "Experiment Split Description is Empty."}
								</Typography>
							</Grid>
							<Grid item xs={12}>
								<InputConfig
									label={`${truncateText(controlSection?.name)} (control)`}
									inputType="number"
									inputStyles={{ width: '100%', margin: '1rem 0' }}
									labelStyles={commonLabelStyles}
									isObjectEditor={false}
									directValue={getSplitPercentage(controlSection)}
									updateDirectValue={(value) => {
										updateExperimentTypeSplitPercentage(controlSection.sectionRef, Number(value));
									}}
								/>
							</Grid>

							<Grid item xs={12}>
								<InputConfig
									label={truncateText(experimentSection.name)}
									inputType="number"
									inputStyles={{ width: '100%', margin: '1rem 0' }}
									labelStyles={commonLabelStyles}
									isObjectEditor={false}
									directValue={getSplitPercentage(experimentSection)}
									updateDirectValue={(value) => {
										updateExperimentTypeSplitPercentage(
											experimentSection.sectionRef,
											Number(value)
										);
									}}
								/>
							</Grid>
						</Grid>
					)}

					{/* Video Section Split Card for NORMAL_SPLIT */}
					{sectionSplitData.type === NORMAL_SPLIT && (
						<Grid container spacing={1} alignItems="center" style={{ width: '100%' }}>
							<Grid item xs={12} >
								<Typography variant='h6'>Split Description</Typography>
								<Typography style={{ fontSize: '1.7rem', color: '#514848', fontWeight: 400 }}>
									{sectionSplitData?.splitDescription ?? "Normal Split Description is Empty."}
								</Typography>
							</Grid>

							{/* Section Name */}
							<Grid item xs={12}>
								<InputConfig
									label={`${truncateText(controlSection?.name)}`}
									inputType="number"
									inputStyles={{ width: '100%', margin: '1rem 0' }}
									labelStyles={commonLabelStyles}
									isObjectEditor={false}
									directValue={sectionSplitData.splitPercentage}
									updateDirectValue={(value) => {
										updateNormalTypeSplitPercentage(videoSectionId, Number(value));
									}}
								/>
							</Grid>

							{/* Delete Button */}
							<Grid item xs={12} style={{ display: 'flex', alignItems: 'center' }}>
								<DeleteOutlinedIcon
									color="primary"
									onClick={() => deleteSectionFromSplitConfig(videoSectionId)}
									className={commonStyleClasses.icon}
									style={{ cursor: 'pointer' }} // Ensures it's clickable
								/>
							</Grid>
						</Grid>
					)}

					{/* Video Section Split Card for MULTI_SPLIT */}
					{sectionSplitData.type === MULTI_SPLIT && (
						<Grid
							container
							spacing={1}
							alignItems="center"
							style={{ width: '100%', marginTop: '1rem' }}
						>
							<Grid item xs={12} >
								<Typography variant='h6'>Split Description</Typography>
								<Typography style={{ fontSize: '1.7rem', color: '#514848', fontWeight: 400 }}>
									{multiSplitData?.comment ?? "Multi Split Description is Empty."}
								</Typography>
							</Grid>

							{/* Control Section */}
							<Grid item xs={12}>
								<InputConfig
									label={`${truncateText(controlSection?.name)}  (control)`}
									inputType="number"
									inputStyles={{ width: '100%', margin: '1rem 0' }}
									labelStyles={commonLabelStyles}
									isObjectEditor={false}
									directValue={getSplitPercentage(controlSection)}
									updateDirectValue={(value) => {
										updateExperimentTypeSplitPercentage(controlSection.sectionRef, Number(value));
									}}
								/>
							</Grid>

							{/* Multi-Split Sections */}
							{multiSplitSections.length > 0 &&
								multiSplitSections.map((section, index) => (
									<Grid item xs={12} key={index}>
										<InputConfig
											label={truncateText(getSectionNameToDisplay(section))}
											inputType="number"
											inputStyles={{ width: '100%', margin: '1rem 0' }}
											labelStyles={commonLabelStyles}
											isObjectEditor={false}
											directValue={getSplitPercentage(section)}
											updateDirectValue={(value) => {
												updateExperimentTypeSplitPercentage(section.sectionRef, Number(value));
											}}
										/>
									</Grid>
								))}
						</Grid>
					)}
				</Box>
			)}
		</Box>
	);
};

export default VideoSectionSplitCard;
